import React, { useEffect, useMemo, useState } from "react";

import ChartGranularityToggle from "./ChartGranularityToggle";
import { getDefaultOverviewChartGranularity } from "./chartsHelpers";

/**
 * Shared shell for Project Overview metric charts (granularity toggle + loading/empty states).
 *
 * @param {Object} props
 * @param {Array<Object>} [props.days] - Aggregated day rows for the active filters.
 * @param {{key?: string, start: string|null, end: string|null}|null} [props.range] - Active Range filter.
 * @param {string} [props.typeLabel="All"] - Active Type name for chart dataset labels.
 * @param {boolean} [props.isLoading] - Whether source metrics are still loading.
 * @param {string} props.emptyMessage - Message when the filtered range has no data points.
 * @param {(days: Array<Object>, range: Object|null, granularity: string, typeLabel: string) => Object} props.buildChartData - Chart.js data builder.
 * @param {() => Object} props.getChartOptions - Chart.js options factory.
 * @param {(data: Object, options: Object) => React.ReactNode} props.renderChart - Renders the Chart.js instance.
 * @returns {JSX.Element}
 */
const OverviewMetricsChart = ({
  days = [],
  range = null,
  typeLabel = "All",
  isLoading = false,
  emptyMessage,
  buildChartData,
  getChartOptions,
  renderChart,
}) => {
  const [granularity, setGranularity] = useState(() =>
    getDefaultOverviewChartGranularity(range, days)
  );

  // Reset when Range or data span changes (All + < 8 weeks → Daily).
  useEffect(() => {
    setGranularity(getDefaultOverviewChartGranularity(range, days));
  }, [range, days]);

  const data = useMemo(
    () => buildChartData(days, range, granularity, typeLabel),
    [buildChartData, days, range, granularity, typeLabel]
  );
  const options = useMemo(() => getChartOptions(), [getChartOptions]);
  const hasPoints = data.labels.length > 0;

  return (
    <div className="mt-2 rounded-xl border border-gray-200 bg-white px-2 py-2">
      <div className="mb-1 flex items-center justify-end px-1">
        <ChartGranularityToggle
          value={granularity}
          onChange={setGranularity}
        />
      </div>
      <div className="h-[200px]">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-sm font-semibold text-gray-500">
            Loading chart…
          </div>
        ) : !hasPoints ? (
          <div className="flex h-full items-center justify-center text-sm font-semibold text-gray-500">
            {emptyMessage}
          </div>
        ) : (
          renderChart(data, options)
        )}
      </div>
    </div>
  );
};

export default OverviewMetricsChart;
